import { Download, Settings as SettingsIcon } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { AgentWorkspace } from '../features/agents/AgentWorkspace.tsx';
import { useDisplayPreferences } from '../features/display/display-preferences.ts';
import { FocusWorkspace } from '../features/focus/FocusWorkspace.tsx';
import { Sidebar } from '../features/layout/Sidebar.tsx';
import { SyncStatusBadge } from '../features/layout/SyncStatusBadge.tsx';
import { WorkspaceShell } from '../features/layout/WorkspaceShell.tsx';
import { useAppBadge } from '../features/notifications/useAppBadge.ts';
import { OkrWorkspace } from '../features/okrs/OkrWorkspace.tsx';
import { CommandPalette } from '../features/palette/CommandPalette.tsx';
import { ReloadPrompt } from '../features/pwa/ReloadPrompt.tsx';
import { useInstallPrompt } from '../features/pwa/useInstallPrompt.ts';
import { type SearchCommand, TopSearchBar } from '../features/search/TopSearchBar.tsx';
import { SettingsModal } from '../features/settings/SettingsModal.tsx';
import { TaskWorkspace } from '../features/tasks/TaskWorkspace.tsx';
import {
  type TaskDisplayMode,
  type TaskWorkspaceView,
  getTaskDisplayModeLabel,
  getTaskWorkspaceLabel,
  isTaskDisplayMode,
} from '../features/tasks/task-workspace-model.ts';
import { getDocStore } from '../store/doc-store.ts';

export type AppRoute = 'tasks' | 'focus' | 'agents' | 'okrs';

function routeFromPath(pathname: string): AppRoute {
  if (pathname === '/focus') return 'focus';
  if (pathname === '/agents') return 'agents';
  if (pathname === '/okrs') return 'okrs';
  return 'tasks';
}

function taskDisplayModeFromLocation(location: Location): TaskDisplayMode {
  const display = new URLSearchParams(location.search).get('display');
  return isTaskDisplayMode(display) ? display : 'list';
}

function taskViewFromLocation(location: Location, fallbackProjectId: string): TaskWorkspaceView {
  const value = new URLSearchParams(location.search).get('view');
  if (value === 'system:all') return { kind: 'system', id: 'all' };
  if (value === 'system:today') return { kind: 'system', id: 'today' };
  if (value === 'system:tomorrow') return { kind: 'system', id: 'tomorrow' };
  if (value === 'system:week') return { kind: 'system', id: 'week' };
  if (value === 'system:favorites') return { kind: 'system', id: 'favorites' };
  if (value === 'system:trash') return { kind: 'system', id: 'trash' };
  if (value?.startsWith('smart:')) return { kind: 'smart', smartListId: value.slice(6) };
  if (value?.startsWith('tag:')) return { kind: 'tag', tag: decodeURIComponent(value.slice(4)) };
  return { kind: 'project', projectId: fallbackProjectId };
}

function pathFromRoute(route: AppRoute, taskDisplayMode: TaskDisplayMode): string {
  if (route === 'focus') return '/focus';
  if (route === 'agents') return '/agents';
  if (route === 'okrs') return '/okrs';
  return taskDisplayMode === 'list' ? '/tasks' : `/tasks?display=${taskDisplayMode}`;
}

function navigate(route: AppRoute, taskDisplayMode: TaskDisplayMode): void {
  const path = pathFromRoute(route, taskDisplayMode);
  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (currentPath !== path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

function searchQueryFromLocation(location: Location): string {
  return new URLSearchParams(location.search).get('q') ?? '';
}

function writeTaskUrl(taskDisplayMode: TaskDisplayMode, view: TaskWorkspaceView): void {
  const url = new URL(window.location.href);
  url.pathname = '/tasks';
  if (taskDisplayMode === 'list') url.searchParams.delete('display');
  else url.searchParams.set('display', taskDisplayMode);

  if (view.kind === 'project') url.searchParams.delete('view');
  if (view.kind === 'system') url.searchParams.set('view', `system:${view.id}`);
  if (view.kind === 'smart') url.searchParams.set('view', `smart:${view.smartListId}`);
  if (view.kind === 'tag') url.searchParams.set('view', `tag:${encodeURIComponent(view.tag)}`);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function writeSearchUrl(query: string): void {
  const url = new URL(window.location.href);
  if (query.trim()) url.searchParams.set('q', query.trim());
  else url.searchParams.delete('q');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function App(): JSX.Element {
  const store = getDocStore();
  const { preferences: displayPreferences } = useDisplayPreferences();
  const activeProject = store.useActiveProject();
  const projects = store.useProjects();
  const smartLists = store.useSmartLists();
  const allTasks = store.useFocusSnapshot().tasks;
  const uiPreferences = store.useUiPreferences();
  useAppBadge(allTasks, uiPreferences.appBadgeCountMode);
  const { canInstall, install } = useInstallPrompt();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [route, setRoute] = useState<AppRoute>(() => routeFromPath(window.location.pathname));
  const [searchDraft, setSearchDraft] = useState(() => searchQueryFromLocation(window.location));
  const [searchQuery, setSearchQuery] = useState(() => searchQueryFromLocation(window.location));
  const [taskDisplayMode, setTaskDisplayMode] = useState<TaskDisplayMode>(() =>
    taskDisplayModeFromLocation(window.location),
  );
  const [taskView, setTaskView] = useState<TaskWorkspaceView>(() =>
    taskViewFromLocation(window.location, activeProject.id),
  );

  useEffect(() => {
    const next = routeFromPath(window.location.pathname);
    if (next !== route) setRoute(next);
    if (window.location.pathname === '/') {
      window.history.replaceState({}, '', '/tasks');
    }

    const onPopState = (): void => {
      setRoute(routeFromPath(window.location.pathname));
      setTaskDisplayMode(taskDisplayModeFromLocation(window.location));
      setTaskView(taskViewFromLocation(window.location, activeProject.id));
      const nextSearch = searchQueryFromLocation(window.location);
      setSearchDraft(nextSearch);
      setSearchQuery(nextSearch);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeProject.id, route]);

  useEffect(() => {
    let prefix: string | undefined;
    const handler = (event: KeyboardEvent): void => {
      if (isTypingTarget(event.target)) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (event.shiftKey && event.key === '?') {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (key === 't') {
        event.preventDefault();
        document.getElementById('quick-add-input')?.focus();
        return;
      }
      if (key === 'g' || key === '*') {
        event.preventDefault();
        prefix = key;
        window.setTimeout(() => {
          prefix = undefined;
        }, 1200);
        return;
      }
      if (prefix === 'g') {
        event.preventDefault();
        prefix = undefined;
        if (key === 'i') selectTaskView({ kind: 'project', projectId: activeProject.id });
        if (key === 'a') selectTaskView({ kind: 'system', id: 'all' });
        if (key === 't') selectTaskView({ kind: 'system', id: 'today' });
        if (key === 'o') selectTaskView({ kind: 'system', id: 'tomorrow' });
        if (key === 'w') selectTaskView({ kind: 'system', id: 'week' });
        if (key === 'f') selectTaskView({ kind: 'system', id: 'favorites' });
        if (key === 'd') selectTaskView({ kind: 'system', id: 'trash' });
        return;
      }
      if (prefix === '*') {
        event.preventDefault();
        prefix = undefined;
        window.dispatchEvent(new CustomEvent('portfolio-shortcut-select', { detail: key }));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeProject.id]);

  useEffect(() => {
    if (route !== 'tasks') return;
    const taskId = new URLSearchParams(window.location.search).get('task');
    if (!taskId) return;
    const task = store.projection.getTask(taskId);
    if (!task) return;
    if (activeProject.id !== task.projectId) {
      store.setActiveProject(task.projectId);
    }
    setTaskView({ kind: 'project', projectId: task.projectId });
  }, [activeProject.id, route, store]);

  useEffect(() => {
    if (taskView.kind !== 'project') return;
    if (projects.some((project) => project.id === taskView.projectId)) return;
    setTaskView({ kind: 'project', projectId: activeProject.id });
  }, [activeProject.id, projects, taskView]);

  useEffect(() => {
    if (taskView.kind !== 'smart') return;
    if (smartLists.some((smartList) => smartList.id === taskView.smartListId)) return;
    setTaskView({ kind: 'project', projectId: activeProject.id });
  }, [activeProject.id, smartLists, taskView]);

  const projectsById = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects],
  );
  const smartListsById = useMemo(
    () => new Map(smartLists.map((smartList) => [smartList.id, smartList])),
    [smartLists],
  );

  const selectTaskView = (view: TaskWorkspaceView): void => {
    setTaskView(view);
    if (route === 'tasks' || view.kind !== 'project') {
      setRoute('tasks');
      writeTaskUrl(taskDisplayMode, view);
    }
  };

  const applySearch = useCallback((query: string): void => {
    const next = query.trim();
    setSearchDraft(next);
    setSearchQuery(next);
    writeSearchUrl(next);
  }, []);

  const clearSearch = useCallback((): void => {
    setSearchDraft('');
    setSearchQuery('');
    writeSearchUrl('');
  }, []);

  const goToRoute = useCallback(
    (nextRoute: AppRoute): void => {
      clearSearch();
      navigate(nextRoute, taskDisplayMode);
      setRoute(nextRoute);
    },
    [clearSearch, taskDisplayMode],
  );

  const setOkrView = useCallback(
    (nextView: 'list' | 'grid' | 'map' | 'timeline'): void => {
      if (route !== 'okrs') {
        navigate('okrs', taskDisplayMode);
        setRoute('okrs');
      }
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('portfolio-okr-view', { detail: nextView }));
      }, 0);
    },
    [route, taskDisplayMode],
  );

  const commands = useMemo<SearchCommand[]>(
    () => [
      {
        id: 'new-task',
        label: 'New task',
        aliases: ['task', 'add task'],
        run: () => {
          goToRoute('tasks');
          window.setTimeout(() => document.getElementById('quick-add-input')?.focus(), 0);
        },
      },
      {
        id: 'tasks',
        label: 'Go to Tasks',
        aliases: ['tasks', 'task view'],
        run: () => goToRoute('tasks'),
      },
      {
        id: 'okrs',
        label: 'Go to OKRs',
        aliases: ['okrs', 'objectives'],
        run: () => goToRoute('okrs'),
      },
      {
        id: 'focus',
        label: 'Go to Focus',
        aliases: ['focus'],
        run: () => goToRoute('focus'),
      },
      {
        id: 'agents',
        label: 'Go to Agent Control',
        aliases: ['agents', 'planning', 'approvals', 'schedule'],
        run: () => goToRoute('agents'),
      },
      {
        id: 'okr-grid',
        label: 'OKR grid view',
        aliases: ['grid'],
        run: () => setOkrView('grid'),
      },
      {
        id: 'okr-list',
        label: 'OKR list view',
        aliases: ['list'],
        run: () => setOkrView('list'),
      },
      {
        id: 'settings',
        label: 'Open settings',
        aliases: ['settings', 'preferences'],
        run: () => setSettingsOpen(true),
      },
      {
        id: 'reset-local',
        label: 'Reset local data',
        aliases: ['reset', 'wipe local'],
        run: async () => {
          if (!window.confirm('Wipe all local data? This cannot be undone.')) return;
          await store.resetLocalData();
          window.location.reload();
        },
      },
    ],
    [goToRoute, setOkrView, store],
  );

  const headerMeta = useMemo(() => {
    if (route === 'focus') {
      return {
        title: 'Focus',
        subtitle: 'Portfolio-wide ranking tuned to your current energy, urgency, and alignment.',
      };
    }
    if (route === 'agents') {
      return {
        title: 'Agent Control',
        subtitle:
          'Durable plans, calendar proposals, approvals, execution receipts, context, and reviews.',
      };
    }

    if (route === 'okrs') {
      return {
        title: 'OKRs',
        subtitle: `${activeProject.name} objectives, key results, linked tasks, and check-ins.`,
      };
    }

    return {
      title: 'Tasks',
      subtitle: `${getTaskWorkspaceLabel(
        taskView,
        projectsById,
        smartListsById,
      )} · ${getTaskDisplayModeLabel(taskDisplayMode)} view with the detail inspector kept in place.`,
    };
  }, [activeProject.name, projectsById, route, smartListsById, taskDisplayMode, taskView]);

  return (
    <div
      className="app-root"
      data-theme={displayPreferences.visualTheme}
      data-density={displayPreferences.density}
      data-contrast={displayPreferences.highContrast ? 'high' : 'normal'}
      data-reduced-motion={displayPreferences.reducedMotion ? 'true' : 'false'}
      style={
        {
          '--workspace-sidebar-width': `${displayPreferences.sidebarWidth}px`,
          '--task-detail-width': `${displayPreferences.taskDetailWidth}px`,
        } as CSSProperties
      }
    >
      <MobileBottomNav
        route={route}
        onNavigate={(nextRoute) => {
          navigate(nextRoute, taskDisplayMode);
          setRoute(nextRoute);
        }}
      />
      <WorkspaceShell
        title={headerMeta.title}
        subtitle={headerMeta.subtitle}
        mobileTaskLayout={true}
        sidebar={
          <Sidebar
            route={route}
            taskView={taskView}
            mobileHidden={true}
            onNavigate={(nextRoute) => {
              navigate(nextRoute, taskDisplayMode);
              setRoute(nextRoute);
            }}
            onSelectTaskView={selectTaskView}
          />
        }
        headerContent={
          <TopSearchBar
            route={route}
            value={searchDraft}
            resultActive={Boolean(searchQuery)}
            commands={commands}
            onChange={setSearchDraft}
            onSearch={applySearch}
            onClear={clearSearch}
            trailingActions={
              <div className="flex shrink-0 items-center gap-2">
                <SyncStatusBadge />
                {canInstall && (
                  <button
                    type="button"
                    aria-label="Install app"
                    title="Install app"
                    onClick={install}
                    className="ui-button ui-button-primary h-9 w-9 px-0"
                  >
                    <Download aria-hidden="true" size={15} />
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Open settings"
                  title="Open settings"
                  onClick={() => setSettingsOpen(true)}
                  className="ui-button h-9 w-9 px-0"
                >
                  <SettingsIcon aria-hidden="true" size={15} />
                </button>
              </div>
            }
          />
        }
      >
        {route === 'tasks' ? (
          <TaskWorkspace
            view={taskView}
            displayMode={taskDisplayMode}
            onChangeView={selectTaskView}
            onChangeDisplayMode={(nextMode) => {
              setTaskDisplayMode(nextMode);
              writeTaskUrl(nextMode, taskView);
            }}
            onOpenSettings={() => setSettingsOpen(true)}
            searchQuery={searchQuery}
            onClearSearch={clearSearch}
          />
        ) : route === 'focus' ? (
          <FocusWorkspace />
        ) : route === 'agents' ? (
          <AgentWorkspace />
        ) : (
          <OkrWorkspace
            searchQuery={searchQuery}
            onClearSearch={clearSearch}
            onOpenTask={(taskId) => {
              const task = store.projection.getTask(taskId);
              if (task) store.setActiveProject(task.projectId);
              setRoute('tasks');
              window.history.pushState({}, '', `/tasks?task=${taskId}`);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          />
        )}
      </WorkspaceShell>
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onSwitchProject={(projectId) => {
          store.setActiveProject(projectId);
          selectTaskView({ kind: 'project', projectId });
        }}
        onSwitchTaskDisplay={(mode) => {
          setRoute('tasks');
          setTaskDisplayMode(mode);
          writeTaskUrl(mode, taskView);
        }}
      />
      <ReloadPrompt />
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function HelpModal({ onClose }: { onClose(): void }): JSX.Element {
  return (
    <div
      className="mobile-safe-overlay fixed inset-0 z-50 flex items-start justify-center bg-[color:var(--app-modal-overlay)]"
      tabIndex={-1}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <section
        data-testid="shortcut-help"
        className="mt-10 max-h-[80vh] w-full max-w-3xl overflow-auto border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-5 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-[color:var(--app-text)]">Keyboard help</h2>
          <button type="button" onClick={onClose} className="ui-button ui-button-subtle">
            Close
          </button>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <HelpSection
            title="Actions"
            items={[
              ['t', 'New task'],
              ['/', 'Search and commands'],
              ['?', 'Open this help'],
              ['j / k', 'Move selection'],
              ['c or x', 'Complete selected task'],
              ['Delete', 'Move selected task to Trash'],
              ['1-4', 'Set priority'],
              ['e', 'Edit title'],
              ['z', 'Undo last local change'],
            ]}
          />
          <HelpSection
            title="Navigation"
            items={[
              ['g then i', 'Inbox/current project'],
              ['g then a', 'All tasks'],
              ['g then t', 'Today'],
              ['g then o', 'Tomorrow'],
              ['g then w', 'This week'],
              ['g then f', 'Favourites'],
              ['g then d', 'Trash'],
              ['* then a', 'Select all visible'],
              ['* then n', 'Clear selection'],
            ]}
          />
          <HelpSection
            title="Fields"
            items={[
              ['d', 'Set due date'],
              ['s', 'Set scheduled date'],
              ['f', 'Edit tags'],
              ['h', 'Switch incomplete/completed'],
              ['p', 'Postpone by one day'],
              ['u', 'Edit URL'],
              ['w', 'Set scheduled date'],
              ['o', 'Edit owner'],
              ['r', 'Edit repeat rule'],
              ['l', 'Edit location'],
              ['b', 'Edit reminder'],
              ['y', 'Focus notes'],
              ['m', 'Edit estimate'],
              ['.', 'More selected actions'],
            ]}
          />
          <HelpSection
            title="Smart Add"
            items={[
              ['!', 'Priority'],
              ['~', 'Start date'],
              ['^', 'Due date'],
              ['*', 'Repeat'],
              ['=', 'Estimate'],
              ['#', 'List/Tags'],
              ['@', 'Location'],
              ['+', 'Give to'],
              ['//', 'Note'],
              ['/', 'Location (legacy)'],
              ['@low/@med/@high', 'Energy (legacy)'],
              ['%', 'Daypart (legacy)'],
              ['>', 'Project/List (legacy)'],
            ]}
          />
          <HelpSection title="Workspace search" items={WORKSPACE_SEARCH_HELP} />
          <HelpSection title="Saved view queries" items={SAVED_VIEW_QUERY_HELP} />
        </div>
      </section>
    </div>
  );
}

const WORKSPACE_SEARCH_HELP: Array<[string, string]> = [
  ['date:<date>', 'Due or scheduled on a date'],
  ['due:<date>', 'Due on a date'],
  ['scheduled:<date>', 'Scheduled on a date'],
  ['priority:<1-3>', 'Priority'],
  ['tag:<tag>', 'Tag'],
  ['project:<id>', 'Project'],
  ['owner:<name>', 'Owner'],
  ['estimate:<minutes>', 'Estimate'],
  ['location:<text>', 'Location'],
  ['energy:<level>', 'Energy'],
  ['schedule:, start:', 'Scheduled-date aliases'],
  ['tags:, list:', 'Tag and project aliases'],
];

const SAVED_VIEW_QUERY_HELP: Array<[string, string]> = [
  ['due:<date>, dueOn:<date>', 'Due on a date'],
  ['dueBefore:<date>', 'Due before a date'],
  ['dueAfter:<date>', 'Due after a date'],
  ['scheduled:<date>, scheduledOn:<date>', 'Scheduled on a date'],
  ['scheduledBefore:<date>', 'Scheduled before a date'],
  ['scheduledAfter:<date>', 'Scheduled after a date'],
  ['dueWithin:<period>', 'Due within a period'],
  ['scheduledWithin:<period>', 'Scheduled within a period'],
  ['added/updated/completedWithin:<period>', 'Timestamp windows'],
  ['priority:<1-3|*>', 'Priority'],
  ['tag:<tag|*>', 'Tag'],
  ['tagContains:<text>', 'Tag text contains'],
  ['project:<id|*>', 'Project'],
  ['listContains:<text>', 'Project/list text contains'],
  ['owner:<name|*>', 'Owner'],
  ['estimate:<minutes|*>', 'Estimate'],
  ['timeEstimate:<minutes|*>', 'Estimate alias'],
  ['location:<text|*>', 'Location'],
  ['hasNotes, hasURL', 'Tasks with notes or URL'],
  ['hasSubtasks, isSubtask', 'Subtask predicates'],
  ['postponed:<count>', 'RTM postponed count'],
  ['source:<text>', 'Imported task source'],
  ['url[:text]', 'Has URL or URL contains text'],
  ['text:<text>, title:<text>', 'Title, notes, or tag text'],
  ['workflow:<state>', 'Workflow state'],
  ['energy:<level>, daypart:<part>', 'Energy or daypart'],
  ['repeat, recurrence', 'Repeating tasks'],
  ['completed, incomplete', 'Completion state'],
  ['overdue, trashed', 'Overdue or trashed tasks'],
  ['parent:<id>, child, root', 'Task hierarchy'],
  ['hasChildren', 'Tasks with children'],
  ['kr:<id>', 'Linked key result'],
  ['AND, OR, NOT', 'Combine query predicates'],
  ['list:, tags:, name:', 'Project, tag, and title aliases'],
  ['beforeDue:, afterDue:', 'Due comparison aliases'],
  ['start:, startBefore:, startAfter:', 'Scheduled-date aliases'],
];

function HelpSection({
  title,
  items,
}: {
  title: string;
  items: Array<[string, string]>;
}): JSX.Element {
  return (
    <section>
      <h3 className="ui-label">{title}</h3>
      <dl className="mt-2 space-y-2">
        {items.map(([keys, label]) => (
          <div key={keys} className="flex items-center justify-between gap-3 text-sm">
            <dt className="font-medium text-[color:var(--app-text)]">{label}</dt>
            <dd className="ui-kbd">{keys}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function MobileBottomNav({
  route,
  onNavigate,
}: {
  route: AppRoute;
  onNavigate(route: AppRoute): void;
}): JSX.Element {
  const tabs: Array<{ id: AppRoute; label: string; icon: string; testId: string }> = [
    { id: 'tasks', label: 'Tasks', icon: '☰', testId: 'mobile-nav-tasks' },
    { id: 'focus', label: 'Focus', icon: '◎', testId: 'mobile-nav-focus' },
    { id: 'agents', label: 'Agents', icon: '✦', testId: 'mobile-nav-agents' },
    { id: 'okrs', label: 'OKRs', icon: '◈', testId: 'mobile-nav-okrs' },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label="Mobile workspace navigation"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[color:var(--app-border)] bg-[color:var(--app-mobile-bar-bg)] backdrop-blur-sm md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="grid h-14 grid-cols-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-testid={tab.testId}
            onClick={() => onNavigate(tab.id)}
            className={`flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors ${
              route === tab.id
                ? 'text-[color:var(--app-accent)]'
                : 'text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text)]'
            }`}
          >
            <span className="text-base leading-none">{tab.icon}</span>
            <span className="leading-none">{tab.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
